/**
 * **C05 · The budget book** (Stream C, Documents.dc.html): the summary in front, then every line
 * by area, laid out by the `budget_book` engine from Budget's festival level and its rules, the
 * meeting day from the finance year, and each area's chairs from Roles. Scored against the
 * drawing by walk 99g.
 */
import type { ReactNode } from 'react';
import { FestivalClock, budgetBook, type BookFestival, type BookLine } from '@cider/domain/engines';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';
import type { RoleRow } from './aod-package.js';

export function BudgetBook({ fy }: { fy: number }): ReactNode {
  const festival = useLoad<BookFestival>(`/money/budget/${String(fy)}/festival`);
  const rules = useLoad<{ receipts: BookLine[]; costs: BookLine[] }>(
    `/money/budget/${String(fy)}/rules`,
  );
  // The meeting that ratifies a year's budget is in the February before it (the year before's card).
  const finance = useLoad<{ steps: { key: string; due: string | null }[] }>(
    `/money/finance-year/${String(fy - 1)}`,
  );
  const roles = useLoad<(RoleRow & { areaName: string | null })[]>(
    `/admin/roles/year?year=${String(fy - 1)}`,
  );
  const loads = [festival, rules, finance, roles];
  const ready = loads.every((l) => l.data !== null || l.error !== null);
  usePrinted('doc-budget-book', ready, { year: fy - 1 });
  if (!ready) return <p>Reading the figures…</p>;
  const chairs: Record<string, string[]> = {};
  for (const r of roles.data ?? [])
    if (
      r.role === 'area_chair' &&
      r.areaName &&
      r.named !== false &&
      (r.state === 'granted' || r.state === 'active')
    )
      (chairs[r.areaName] ??= []).push(r.name);
  const pages = budgetBook({
    fiscalYear: fy,
    day: FestivalClock.calendarDay(new Date()),
    meeting: finance.data?.steps.find((s) => s.key === 'meeting')?.due ?? null,
    festival: festival.data,
    lines: rules.data,
    chairs,
  });
  return <DrawnPages pages={pages} />;
}
